import {
  buildingTypeSchema,
  shortAddressString,
  type AssetMedia,
  type AssetPricesPayload,
  type AssetRelationType,
  type BuildingType,
} from '@aikoa/common'
import { getCurrentLocale } from '@aikoa/i18n'
import { useTranslations } from '@aikoa/i18n/react'
import { useCallback, useMemo, useState } from 'react'
import { TextInput, View } from 'react-native'
import Animated, { FadeIn, LinearTransition } from 'react-native-reanimated'
import { z } from 'zod/v4'
import { PriceEditor } from '~/components/assets/PriceEditor'
import CloseButton from '~/components/CloseButton'
import { FileUpload } from '~/components/FileUpload'
import { PhaseWrapper } from '~/components/flows/phases/PhaseWrapper'
import Icon from '~/components/Icon'
import { SingleSelectChips } from '~/components/interests/inputs/SingleSelectChips'
import Image from '~/components/layout/Image'
import { AddressAutocomplete } from '~/components/locations/AddressAutocomplete'
import { Label } from '~/components/typography/Content'
import { Button, ButtonLabel } from '~/components/ui/button'
import {
  GuidedFormSummary,
  type InputSegment as InputSegmentType,
  type ScriptSegment,
} from '~/components/ui/guided-form-summary'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { getBuildingTypeLabel } from '~/lib/assets'
import { propertyInputFormSchema, type PropertyInputData } from '~/lib/propertyFormSchemas'

export { propertyInputFormSchema as manualInputSchema, type PropertyInputData }

const locale = getCurrentLocale()

type InputSegmentName =
  | 'relationType'
  | 'address'
  | 'buildingType'
  | 'size'
  | 'prices'
  | 'rooms'
  | 'buildYear'
  | 'media'
  | 'listingLink'

const urlSchema = z.url()

const parseNumericInputValue = (inputValue: string) => {
  const digitOnlyValue = inputValue.replace(/[^0-9]/g, '')

  if (digitOnlyValue.length === 0) {
    return undefined
  }

  const normalizedValue = inputValue.replace(',', '.')
  const parsedValue = Number.parseFloat(normalizedValue)

  return !Number.isNaN(parsedValue) && Number.isFinite(parsedValue) ? parsedValue : undefined
}

export function ManualPropertyInputPhase({
  data,
  onUpdateData,
  mode = 'create',
}: {
  data: Partial<PropertyInputData>
  onUpdateData: (data: Partial<PropertyInputData>) => void
  mode?: 'create' | 'edit'
}) {
  const isEditMode = mode === 'edit'
  const { t } = useTranslations('property')
  const { t: tValidation } = useTranslations('validation')

  const RELATION_TYPE_OPTIONS: { value: AssetRelationType; label: string }[] = useMemo(
    () => [
      {
        value: 'prospect',
        label: t(($) => $.flow.manualInput.values.prospect),
      },
      {
        value: 'reference',
        label: t(($) => $.flow.manualInput.values.reference),
      },
      {
        value: 'representation',
        label: t(($) => $.flow.manualInput.values.representation),
      },
    ],
    [t]
  )

  const [activatedSegments, setActivatedSegments] = useState<Set<InputSegmentName>>(() => {
    const initial = new Set<InputSegmentName>()

    if (isEditMode) {
      initial.add('relationType')
      initial.add('address')
      initial.add('buildingType')
      initial.add('size')
      initial.add('prices')
      initial.add('rooms')
      initial.add('buildYear')

      if (data.relationType === 'representation') {
        initial.add('listingLink')
      }
    } else {
      initial.add('relationType')
      initial.add('address')

      if (data.buildingType) {
        initial.add('buildingType')
      }

      if (data.size) {
        initial.add('size')
      }

      if (data.prices) {
        initial.add('prices')
      }

      if (data.rooms) {
        initial.add('rooms')
      }

      if (data.buildYear) {
        initial.add('buildYear')
      }

      if (data.media && data.media.length > 0) {
        initial.add('media')
      }

      if (data.listingLink) {
        initial.add('listingLink')
      }
    }

    return initial
  })

  const isRepresentation = data.relationType === 'representation'
  const isProspect = data.relationType === 'prospect'

  const [selectedEditor, setSelectedEditor] = useState<InputSegmentName | undefined>(() => {
    if (isEditMode) {
      return undefined
    }

    return isProspect ? 'address' : 'relationType'
  })

  const [notesExpanded, setNotesExpanded] = useState(!!data.notes)

  const handleOpenEditor = useCallback((editor: InputSegmentName | undefined) => {
    if (!editor) {
      setSelectedEditor(undefined)
      return
    }

    setActivatedSegments((prev) => new Set(prev).add(editor))
    setSelectedEditor(editor)
  }, [])

  const formatPrice = (prices: AssetPricesPayload | undefined) => {
    if (!prices?.displayPrice) {
      return '???'
    }

    return `${prices.displayPrice.toLocaleString('fi-FI')} €`
  }

  const buildingTypeOptions = useMemo(
    () =>
      buildingTypeSchema.options.map((typeLiteral) => ({
        value: typeLiteral.value,
        label: getBuildingTypeLabel({
          buildingType: typeLiteral.value,
          t: t,
        }),
      })),
    [t]
  )

  const inputs: InputSegmentType<InputSegmentName>[] = useMemo(
    () =>
      [
        {
          name: 'relationType',
          value: data.relationType,
          editor: null,
          isValid: () => !!data.relationType,
          renderValue: () => {
            const option = RELATION_TYPE_OPTIONS.find(
              (option) => option.value === data.relationType
            )

            return option?.label ?? '???'
          },
          readOnly: isEditMode,
        },
        {
          name: 'address',
          value: data.address,
          editor: null,
          isValid: () => !!data.address?.street && !!data.location,
          renderValue: () => (data.address ? shortAddressString(data.address) : '???'),
          readOnly: isEditMode,
        },
        {
          name: 'buildingType',
          value: data.buildingType,
          editor: null,
          isValid: () => !!data.buildingType,
          renderValue: () =>
            data.buildingType
              ? getBuildingTypeLabel({
                  buildingType: data.buildingType,
                  t: t,
                })
              : '???',
        },
        {
          name: 'size',
          value: data.size,
          editor: null,
          isValid: () => !!data.size && data.size > 0,
          hasError: () =>
            data.size !== undefined &&
            !propertyInputFormSchema.shape.size.safeParse(data.size).success,
          renderValue: () =>
            data.size
              ? `${data.size.toLocaleString('fi-FI')} ${t(($) => $.flow.manualInput.sizeSuffix)}`
              : '???',
        },
        {
          name: 'prices',
          value: data.prices,
          editor: null,
          isValid: () => !!data.prices?.displayPrice && data.prices.displayPrice > 0,
          renderValue: () => formatPrice(data.prices),
        },
        {
          name: 'rooms',
          value: data.rooms,
          editor: null,
          isValid: () => !!data.rooms && data.rooms > 0,
          hasError: () =>
            data.rooms !== undefined &&
            !propertyInputFormSchema.shape.rooms.safeParse(data.rooms).success,
          renderValue: () =>
            data.rooms
              ? `${data.rooms} ${t(($) => $.flow.manualInput.rooms, {
                  count: data.rooms,
                })}`
              : '???',
          optional: true,
        },
        {
          name: 'buildYear',
          value: data.buildYear,
          editor: null,
          isValid: () => !!data.buildYear && data.buildYear > 1800,
          hasError: () =>
            data.buildYear !== undefined &&
            !propertyInputFormSchema.shape.buildYear.safeParse(data.buildYear).success,
          renderValue: () =>
            data.buildYear
              ? `${t(($) => $.flow.manualInput.buildYearPrefix)}${data.buildYear}`
              : '???',
          optional: true,
        },
        {
          name: 'media',
          value: data.media,
          editor: null,
          isValid: () => !!data.media && data.media.length > 0,
          renderValue: () => <MediaValueThumbnails media={data.media ?? []} />,
          optional: true,
        },
        isRepresentation && {
          name: 'listingLink',
          value: data.listingLink,
          editor: null,
          isValid: () => {
            if (!data.listingLink) {
              return true
            }

            return urlSchema.safeParse(data.listingLink).success
          },
          renderValue: () => {
            if (!data.listingLink) {
              return '???'
            }

            try {
              const url = new URL(data.listingLink)
              return url.hostname
            } catch {
              return data.listingLink
            }
          },
          optional: true,
        },
      ].filter((i) => !!i) as InputSegmentType<InputSegmentName>[],
    [data, RELATION_TYPE_OPTIONS, t, isEditMode, isRepresentation]
  )

  const script: ScriptSegment<InputSegmentName>[] = useMemo(
    () =>
      [
        {
          name: 'relationType',
          segmentScript: t(($) => $.flow.manualInput.script.relationType),
          buttonLabel: t(($) => $.flow.manualInput.buttonLabels.relationType),
        },
        {
          name: 'address',
          segmentScript: t(($) => $.flow.manualInput.script.address),
          buttonLabel: t(($) => $.flow.manualInput.buttonLabels.address),
        },
        {
          name: 'buildingType',
          segmentScript: t(($) => $.flow.manualInput.script.buildingType),
          buttonLabel: t(($) => $.flow.manualInput.buttonLabels.buildingType),
        },
        {
          name: 'size',
          segmentScript: t(($) => $.flow.manualInput.script.size),
          buttonLabel: t(($) => $.flow.manualInput.buttonLabels.size),
        },
        {
          name: 'prices',
          segmentScript: t(($) =>
            isRepresentation
              ? $.flow.manualInput.script.prices.representation
              : $.flow.manualInput.script.prices.prospect
          ),
          buttonLabel: t(($) =>
            isRepresentation
              ? $.flow.manualInput.buttonLabels.prices.representation
              : $.flow.manualInput.buttonLabels.prices.prospect
          ),
        },
        {
          name: 'rooms',
          segmentScript: t(($) => $.flow.manualInput.script.rooms),
          buttonLabel: t(($) => $.flow.manualInput.buttonLabels.rooms),
        },
        {
          name: 'buildYear',
          segmentScript: t(($) => $.flow.manualInput.script.buildYear),
          buttonLabel: t(($) => $.flow.manualInput.buttonLabels.buildYear),
        },
        {
          name: 'media',
          segmentScript: t(($) => $.flow.manualInput.script.media),
          buttonLabel: t(($) => $.flow.manualInput.buttonLabels.media),
        },
        isRepresentation && {
          name: 'listingLink',
          segmentScript: t(($) => $.flow.manualInput.script.listingLink),
          buttonLabel: t(($) => $.flow.manualInput.buttonLabels.listingLink),
        },
      ].filter((i) => !!i) as ScriptSegment<InputSegmentName>[],
    [isRepresentation, t]
  )

  const getEditor = useCallback(
    (name: InputSegmentName) => {
      switch (name) {
        case 'relationType': {
          return (
            <Text className="text-base">{t(($) => $.flow.manualInput.prospectDescription)}</Text>
          )

          /*
          const option = RELATION_TYPE_OPTIONS.find(
            (option) => option.value === data.relationType
          )
          const label = option?.label ?? '???'
          return isEditMode ? (
            <Text className="text-center text-xs uppercase">
              Kohde on {label}
            </Text>
          ) : (
            <SingleSelectChips
              options={RELATION_TYPE_OPTIONS}
              selected={data.relationType}
              onSelect={(value) => {
                onUpdateData({
                  ...data,
                  relationType: value as RelationType,
                })
              }}
            />
          ) */
        }
        case 'address':
          return isEditMode ? (
            <Text className="text-center text-xs uppercase">
              {t(($) => $.flow.manualInput.addressLabel)}{' '}
              {data.address ? shortAddressString(data.address) : '???'}
            </Text>
          ) : (
            <AddressAutocomplete
              inputVariant="line-dark"
              placeholder={t(($) => $.flow.manualInput.addressPlaceholder)}
              value={
                data.address && data.location
                  ? { address: data.address, location: data.location }
                  : null
              }
              onAddressChange={(result) => {
                onUpdateData({
                  ...data,
                  address: result?.address,
                  location: result?.location,
                })
              }}
            />
          )

        case 'buildingType':
          return (
            <SingleSelectChips
              options={buildingTypeOptions}
              selected={data.buildingType}
              onSelect={(value) => {
                onUpdateData({
                  ...data,
                  buildingType: value as BuildingType,
                })
              }}
            />
          )

        case 'size': {
          const sizeError =
            data.size !== undefined && data.size < locale.property.min_size
              ? tValidation(($) => $.number_too_small, { min: locale.property.min_size })
              : data.size !== undefined && data.size > locale.property.max_size
                ? tValidation(($) => $.number_too_large, { max: locale.property.max_size })
                : null

          return (
            <Input
              type="numeric"
              variant="line-dark"
              placeholder={t(($) => $.flow.manualInput.sizePlaceholder)}
              formatted
              value={String(data.size ?? '')}
              onChangeText={(text) => {
                onUpdateData({ ...data, size: parseNumericInputValue(text) })
              }}
              onEndEditing={() => {
                if (data.size === undefined) {
                  return
                }

                const clamped = Math.min(
                  Math.max(data.size, locale.property.min_size),
                  locale.property.max_size
                )

                if (clamped !== data.size) {
                  onUpdateData({ ...data, size: clamped })
                }
              }}
              suffix={t(($) => $.flow.manualInput.sizeSuffix)}
              inputClassName="text-center font-light text-xl"
              error={sizeError}
            />
          )
        }

        case 'prices': {
          const relationType = (data.relationType ?? 'prospect') as AssetRelationType

          const currentPrices: AssetPricesPayload = data.prices ?? {
            displayPrice: 0,
          }

          return (
            <PriceEditor
              data={currentPrices}
              relationType={relationType}
              onUpdate={(pricesData) => onUpdateData({ ...data, prices: pricesData })}
              variant="inline"
            />
          )
        }

        case 'rooms': {
          const roomsError =
            data.rooms !== undefined && data.rooms < locale.property.min_rooms
              ? tValidation(($) => $.number_too_small, { min: locale.property.min_rooms })
              : data.rooms !== undefined && data.rooms > locale.property.max_rooms
                ? tValidation(($) => $.number_too_large, { max: locale.property.max_rooms })
                : null

          return (
            <Input
              type="numeric"
              variant="line-dark"
              placeholder={t(($) => $.flow.manualInput.roomsPlaceholder)}
              formatted={false}
              value={data.rooms !== undefined ? String(data.rooms) : ''}
              onChangeText={(text) => {
                const parsed = Number.parseInt(text.replace(/\D/g, ''), 10)
                onUpdateData({ ...data, rooms: Number.isNaN(parsed) ? undefined : parsed })
              }}
              onEndEditing={() => {
                if (data.rooms === undefined) {
                  return
                }

                const clamped = Math.min(
                  Math.max(data.rooms, locale.property.min_rooms),
                  locale.property.max_rooms
                )

                if (clamped !== data.rooms) {
                  onUpdateData({ ...data, rooms: clamped })
                }
              }}
              suffix={t(($) => $.flow.manualInput.roomsSuffix)}
              inputClassName="text-center font-light text-xl"
              error={roomsError}
            />
          )
        }

        case 'buildYear': {
          const currentYear = new Date().getFullYear()
          const buildYearError =
            data.buildYear !== undefined && data.buildYear < locale.property.min_year
              ? tValidation(($) => $.number_too_small, { min: locale.property.min_year })
              : data.buildYear !== undefined && data.buildYear > currentYear
                ? tValidation(($) => $.number_too_large, { max: currentYear })
                : null

          return (
            <Input
              type="numeric"
              variant="line-dark"
              placeholder={t(($) => $.flow.manualInput.buildYearPlaceholder)}
              value={data.buildYear !== undefined ? String(data.buildYear) : ''}
              onChangeText={(text) => {
                const parsed = Number.parseInt(text.replace(/\D/g, ''), 10)
                onUpdateData({ ...data, buildYear: Number.isNaN(parsed) ? undefined : parsed })
              }}
              onEndEditing={() => {
                if (data.buildYear === undefined) {
                  return
                }

                const clamped = Math.min(
                  Math.max(data.buildYear, locale.property.min_year),
                  currentYear
                )

                if (clamped !== data.buildYear) {
                  onUpdateData({ ...data, buildYear: clamped })
                }
              }}
              prefix={t(($) => $.flow.manualInput.buildYearPrefix)}
              inputClassName="text-center font-light text-xl"
              error={buildYearError}
            />
          )
        }

        case 'media':
          return (
            <MediaThumbnails
              media={data.media ?? []}
              onMediaUpdate={(newMedia) => onUpdateData({ ...data, media: newMedia })}
            />
          )

        case 'listingLink':
          return (
            <Input
              variant="line-dark"
              placeholder={t(($) => $.flow.manualInput.listingLinkPlaceholder)}
              value={data.listingLink ?? ''}
              onChangeText={(text) => {
                onUpdateData({ ...data, listingLink: text })
              }}
              keyboardType="url"
              autoCapitalize="none"
              inputClassName="text-center font-light text-xl"
            />
          )

        default:
          return null
      }
    },
    [isEditMode, data, buildingTypeOptions, onUpdateData, t, tValidation]
  )

  const renderEditor = useCallback(
    (name: InputSegmentName) => (
      <View className="relative z-50 -mx-3 w-[calc(100%+21px)] gap-4 rounded-content bg-muted px-6 py-4">
        {getEditor(name)}

        {isEditMode && (
          <Button
            variant="link"
            size="sm"
            className="-mb-2"
            onPress={() => handleOpenEditor(undefined)}>
            <ButtonLabel>{t(($) => $.flow.manualInput.close)}</ButtonLabel>
          </Button>
        )}
      </View>
    ),
    [getEditor, handleOpenEditor, isEditMode, t]
  )

  return (
    <PhaseWrapper className="relative w-full gap-10">
      <GuidedFormSummary
        inputs={inputs}
        script={script}
        activatedSegments={Array.from(activatedSegments)}
        selectedSegment={selectedEditor}
        onSelectSegment={handleOpenEditor}
        renderEditor={renderEditor}
        className="relative z-10 gap-6"
      />
      {isProspect && (
        <View className="relative z-0 gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 mt-4 self-center pl-3 pr-4"
            onPress={() => setNotesExpanded(!notesExpanded)}>
            <Icon
              name={notesExpanded ? 'chevron-up' : 'chevron-down'}
              size={16}
              className="mr-1 text-secondary"
            />
            <ButtonLabel className="text-secondary">
              {notesExpanded
                ? t(($) => $.flow.manualInput.notes.toggle.show)
                : t(($) => $.flow.manualInput.notes.toggle.hide)}
            </ButtonLabel>
          </Button>

          {notesExpanded && (
            <Animated.View entering={FadeIn.duration(150)} className="mt-4 gap-2">
              <Label className="text-center text-xs uppercase">
                {t(($) => $.flow.manualInput.notes.label)}
              </Label>
              <TextInput
                multiline
                numberOfLines={4}
                value={data.notes ?? ''}
                onChangeText={(text) => onUpdateData({ ...data, notes: text })}
                placeholder={t(($) => $.flow.manualInput.notes.placeholder)}
                className="min-h-[100px] rounded-lg border border-border p-3 text-foreground"
                textAlignVertical="top"
              />
            </Animated.View>
          )}
        </View>
      )}
      {mode !== 'edit' && <View className="h-6" />}
    </PhaseWrapper>
  )
}

function MediaThumbnails({
  media,
  onMediaUpdate,
}: {
  media: AssetMedia[]
  onMediaUpdate: (newMedia: AssetMedia[]) => void
}) {
  const { t } = useTranslations('property')
  const [showUpload, setShowUpload] = useState(false)

  const handleDelete = (urlToDelete: string) => {
    const updatedMedia = media.filter((item) => item.url !== urlToDelete)

    const reorderedMedia = updatedMedia
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item, index) => ({ ...item, order: index }))

    onMediaUpdate(reorderedMedia)
  }

  return (
    <View className="gap-3">
      {media.length > 0 && (
        <Animated.View layout={LinearTransition.duration(150)} className="flex-row flex-wrap gap-2">
          {media.map((item) => (
            <View key={item.url} className="relative">
              <Image
                source={{ uri: item.url }}
                style={{ width: 80, height: 60, borderRadius: 6 }}
                contentFit="cover"
              />
              <CloseButton
                onPress={() => {
                  handleDelete(item.url)
                  return false
                }}
                className="absolute -right-2 -top-2 size-6 border-0 bg-background"
                iconClassName="text-pink-500 size-2 self-start mr-4"
              />
            </View>
          ))}
        </Animated.View>
      )}

      {media.length < 4 && !showUpload && (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          onPress={() => setShowUpload(true)}>
          <Icon name="add" size={16} className="mr-1" />
          <ButtonLabel>{t(($) => $.flow.manualInput.addImage)}</ButtonLabel>
        </Button>
      )}

      {showUpload && (
        <View className="items-center justify-center gap-2">
          <FileUpload
            bucket="property-media"
            className="items-start"
            order={media.length}
            onUpload={(newMedia) => {
              onMediaUpdate([...media, newMedia])
              setShowUpload(false)
            }}
            onError={(error) => {
              console.error('Upload error:', error)
            }}
            maxSizeBytes={15 * 1024 * 1024}
            multiple={false}
          />
          <Button variant="link" size="sm" onPress={() => setShowUpload(false)}>
            {t(($) => $.flow.manualInput.cancel)}
          </Button>
        </View>
      )}
    </View>
  )
}

function MediaValueThumbnails({ media }: { media: AssetMedia[] }) {
  if (media.length === 0) {
    return '???'
  }

  return (
    <View className="flex-row items-center gap-1">
      {media.slice(0, 4).map((item) => (
        <Image
          key={item.url}
          source={{ uri: item.url }}
          style={{ width: 48, height: 48, borderRadius: 4 }}
          contentFit="cover"
          className="aspect-square"
        />
      ))}
      {media.length > 4 && (
        <View className="size-7 items-center justify-center rounded bg-muted">
          <Text className="text-xs text-secondary">+{media.length - 4}</Text>
        </View>
      )}
    </View>
  )
}
